import React, { useState, useEffect } from 'react'
import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  Box,
  Tooltip,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import {
  createBillingRecord,
  updateBillingRecord,
  fetchMonthlyActualGeneration,
  fetchPlantsForBillingForm
} from '../../../../services/billingRecords.service'
import './billing-form.scss'

const LOCKED_FINANCIAL_STATUSES = ['To Be Raised', 'Paid', 'Pending', 'Unpaid']
const BillingForm = ({ open, onClose, onSave, record, mode }) => {
  const [formData, setFormData] = useState({
    recordDate: dayjs(),
    date: dayjs().endOf('month'),
    dueDate: dayjs().endOf('month').add(10, 'day'),
    capacity: '',
    organization: '',
    organizationId: '',
    billedFrom: 'Inverter',
    actualGeneration: '',
    deemedGeneration: '',
    tariff: '',
    amount: '',
    currency: 'INR',
    status: 'To Be Verified',
    type: 'Regular',
    invoice: '',
    remark: '',
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [plants, setPlants] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [actualGenHelper, setActualGenHelper] = useState('')
  const [userEditedActualGeneration, setUserEditedActualGeneration] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, pendingStatus: null })
  const [originalValues, setOriginalValues] = useState({
    actualGeneration: null,
  })
  const financialFieldsLocked = LOCKED_FINANCIAL_STATUSES.includes(formData.status)

  // Fetch plants list (including hardcoded ones) from the V4 monitoring backend
  useEffect(() => {
    const fetchPlants = async () => {
      try {
        setLoading(true)

        // Use the V4 service function
        const recordDateParam = formData.recordDate ? formData.recordDate.toISOString() : undefined
        const data = await fetchPlantsForBillingForm(recordDateParam)

        const plantList = data.plants || []
        // Ensure each plant has both _id and id properties
        const normalizedPlants = plantList.map(plant => ({
          ...plant,
          _id: plant._id,
          id: plant._id,
        }))
        setPlants(normalizedPlants)
        console.log('Fetched plants for billing form (V4):', normalizedPlants)

        if (mode === 'add' && formData.plantId) {
          const stillAvailable = normalizedPlants.find(p => p.id === formData.plantId || p._id === formData.plantId)
          if (!stillAvailable) {
            setFormData(prev => ({
              ...prev,
              plantId: '',
              plantName: '',
              capacity: '',
            }))
          }
        }
      } catch (error) {
        console.error('Error fetching plants:', error)
      } finally {
        setLoading(false)
      }
    }

    if (open) {
      fetchPlants()
    }
  }, [open, formData.recordDate, mode])

  useEffect(() => {
    if (record && mode === 'edit') {
      setFormData({
        plantId: record.plantId || '',
        plantName: record.plantName || '',
        invoiceNumber: record.invoice || '',
        recordDate: record.recordDate ? dayjs(record.recordDate) : null,
        date: record.date ? dayjs(record.date) : null,
        dueDate: record.dueDate ? dayjs(record.dueDate) : dayjs().add(7, 'day'),
        capacity: record.capacity || '',
        organization: record.organization || '',
        organizationId: record.organizationId || '',
        billedFrom: record.billedFrom || 'Inverter',
        actualGeneration: record.actualGeneration != null ? String(parseFloat(record.actualGeneration)) : '',
        deemedGeneration: record.deemedGeneration != null ? String(parseFloat(record.deemedGeneration)) : '',
        tariff: record.tariff != null ? String(parseFloat(record.tariff)) : '',
        amount: record.amount != null ? String(parseFloat(record.amount)) : '',
        currency: record.currency || 'INR',
        status: record.status || 'To Be Verified',
        type: record.type || 'Regular',
        invoice: record.invoice || '',
        remark: record.remark || '',
      })
      // Store original values for comparison
      setOriginalValues({
        actualGeneration: record.actualGeneration != null ? String(parseFloat(record.actualGeneration)) : null,
      })
    } else {
      setFormData({
        plantId: '',
        plantName: '',
        invoiceNumber: '',
        recordDate: dayjs(),
        date: dayjs().endOf('month'),
        dueDate: dayjs().endOf('month').add(10, 'day'),
        capacity: '',
        organization: '',
        organizationId: '',
        billedFrom: 'Inverter',
        actualGeneration: '',
        deemedGeneration: '',
        tariff: '',
        amount: '',
        currency: 'INR',
        status: 'To Be Verified',
        type: 'Regular',
        invoice: '',
        remark: '',
      })
      setOriginalValues({
        actualGeneration: null,
      })
    }
    setErrors({})
  }, [record, mode, open])

  const handleChange = (field, value) => {
    if (field === 'recordDate' && value) {
      const invoiceDate = value.endOf('month');
      const dueDate = invoiceDate.add(10, 'day');
      setFormData(prev => ({
        ...prev,
        recordDate: value,
        date: invoiceDate,
        dueDate: dueDate
      }));
      return;
    }

    if (field === 'status' && value === 'To Be Raised') {
      setConfirmDialog({ open: true, pendingStatus: value })
      return
    }

    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' })
    }

    // Auto-calculate amount
    if (field === 'actualGeneration' || field === 'deemedGeneration' || field === 'tariff') {
      if (field === 'actualGeneration') {
        setUserEditedActualGeneration(true)
      }
      const actual =
        field === 'actualGeneration' ? value : formData.actualGeneration
      const deemed = field === 'deemedGeneration' ? value : formData.deemedGeneration
      const tariff = field === 'tariff' ? value : formData.tariff
      const totalGeneration = (parseFloat(actual) || 0) + (parseFloat(deemed) || 0)

      if (tariff) {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
          amount: (parseFloat(totalGeneration) * parseFloat(tariff)).toFixed(2),
        }))
      }
    }
  }

  const handleConfirmStatusChange = () => {
    setFormData({ ...formData, status: confirmDialog.pendingStatus })
    setConfirmDialog({ open: false, pendingStatus: null })
  }

  const handleCancelStatusChange = () => {
    setConfirmDialog({ open: false, pendingStatus: null })
  }

  //get actual generation from dgr
  useEffect(() => {
    const run = async () => {
      if (mode !== 'add') return
      if (!formData.plantId || !formData.recordDate) return
      if (userEditedActualGeneration) return

      const sum = await fetchMonthlyActualGeneration(formData.plantId, formData.recordDate)

      if (sum > 0) {
        setFormData((prev) => ({
          ...prev,
          actualGeneration: String(sum),
          amount: prev.tariff
            ? ((Number(sum) + Number(prev.deemedGeneration || 0)) * parseFloat(prev.tariff)).toFixed(2)
            : prev.amount,
        }))
        setOriginalValues((prev) => ({
          ...prev,
          actualGeneration: String(sum),
        }))
        setActualGenHelper('Auto-filled from Daily Generation Reports for the selected month. You can edit this value.')
      } else {
        //no dgr generation found
        setFormData((prev) => ({ ...prev, actualGeneration: '' }))
        setOriginalValues((prev) => ({
          ...prev,
          actualGeneration: null,
        }))
        setActualGenHelper('No Daily Generation Reports found for the selected month. Defaulted to 0. You can edit this value.')
      }
    }
    run()
  }, [formData.plantId, formData.recordDate, mode, userEditedActualGeneration])

  const handlePlantChange = (plantId) => {
    const selectedPlant = plants.find((plant) => plant.id === plantId || plant._id === plantId)
    setFormData((prev) => ({
      ...prev,
      plantId: plantId,
      plantName: selectedPlant ? selectedPlant.plantName : '',
      capacity: selectedPlant ? (selectedPlant.plantSize || '') : '',
      organizationId: selectedPlant?.organizationId || '',
      organization: selectedPlant?.organizationId || '',
      tariff: selectedPlant?.tariff != null ? String(selectedPlant.tariff) : prev.tariff,
    }))

    //Clear error when plant is selected
    if (errors.plantId) {
      setErrors((prev) => ({
        ...prev,
        plantId: '',
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.plantId) newErrors.plantId = 'Plant is required'
    if (!formData.recordDate) newErrors.recordDate = 'Record date is required'
    if (!formData.billedFrom) newErrors.billedFrom = 'Billed from is required'
    if (!formData.actualGeneration)
      newErrors.actualGeneration = 'Energy value is required'
    if (!formData.tariff) newErrors.tariff = 'Tariff is required'
    if (!formData.amount) newErrors.amount = 'Invoice value is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setLoading(true)
    try {
      const payload = {
        invoiceNumber: formData.invoiceNumber || undefined,
        plantId: formData.plantId,
        billedFrom: formData.billedFrom,
        type: formData.type,
        actualGeneration: parseFloat(formData.actualGeneration) || 0,
        deemedGeneration: parseFloat(formData.deemedGeneration) || 0,
        tariff: parseFloat(formData.tariff) || 0,
        amount: parseFloat(formData.amount) || 0,
        currency: formData.currency,
        paymentStatus: formData.status,
        remarks: formData.remark || '',
        recordDate: formData.recordDate?.toISOString(),
        invoiceDate: formData.date?.toISOString() || undefined,
        dueDate: formData.dueDate?.toISOString() || undefined,
      }

      console.log('📝 Submitting billing record form with:', payload)

      let result;
      if (mode === 'edit') {
        // Use id or _id (fallback for MongoDB documents)
        const recordId = record.id || record._id
        if (!recordId) {
          throw new Error('Record ID is missing. Cannot update billing record.')
        }
        console.log(`📝 Updating billing record with ID: ${recordId}`)
        result = await updateBillingRecord(recordId, payload)
      } else {
        result = await createBillingRecord(payload)
      }

      setSnackbar({
        open: true,
        message: `Billing record ${mode === 'edit' ? 'updated' : 'created'} successfully`,
        severity: 'success',
      })

      // Call parent onSave to refresh the list
      if (onSave) {
        onSave(result)
      }

      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (error) {
      console.error('Error saving billing record:', error)
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save billing record',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return;
          onClose();
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        className="billing-form-dialog"
      >
        <DialogTitle>
          <div className="dialog-header">
            <span>{mode === 'add' ? 'Add Record' : 'Edit Record'}</span>
            <IconButton onClick={onClose} size="small" disabled={loading}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Record Date"
                      value={formData.recordDate}
                      onChange={(newValue) => handleChange('recordDate', newValue)}
                      views={['month', 'year']}
                      format="MM/YYYY"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.recordDate,
                          helperText: errors.recordDate,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                disablePortal
                PopperProps={{
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: false,
                    },
                  ],
                }}
                loading={loading}
                options={plants}
                getOptionLabel={(option) =>
                  option.projectCode
                    ? `${option.plantName} - ${option.projectCode}`
                    : option.plantName
                }
                value={plants.find((p) => p._id === formData.plantId || p.id === formData.plantId) || null}
                onChange={(e, newValue) =>
                  handlePlantChange(newValue ? (newValue.id || newValue._id) : '')
                }
                isOptionEqualToValue={(option, value) =>
                  (option._id === value._id) || (option.id === value.id)
                }
                disabled={mode === 'edit'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Plant"
                    required
                    error={!!errors.plantId}
                    helperText={errors.plantId}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  loading ? 'Fetching plants...' : 'No plants found.'
                }
                filterOptions={(options, { inputValue }) =>
                  options.filter((plant) =>
                    `${plant.plantName} ${plant.projectCode || ''}`
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  )
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capacity (kWp)"
                type="number"
                value={formData.capacity}
                onChange={(e) => handleChange('capacity', e.target.value)}
                InputProps={{ readOnly: true }}
                title={formData.organizationId ? `Organization ID: ${formData.organizationId}` : 'No organization assigned'}
              />
            </Grid>


            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Invoice Number"
                value={formData.invoiceNumber}
                onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                error={!!errors.invoiceNumber}
                helperText={mode === 'add' ? 'Will be generated by Finance Team' : errors.invoiceNumber}
                disabled={mode === 'add' || formData.status === "To Be Raised"}
                placeholder={mode === 'add' ? 'Will be generated by Finance Team' : ''}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Invoice Date"
                  value={formData.date}
                  disabled={true}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: 'Auto-calculated from Record Month',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Due Date"
                  value={formData.dueDate}
                  disabled={true}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dueDate,
                      helperText: 'Auto-calculated (Invoice Date + 10 days)',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.billedFrom}>
                <InputLabel>Billed From</InputLabel>
                <Select
                  value={formData.billedFrom}
                  onChange={(e) => handleChange('billedFrom', e.target.value)}
                  label="Billed From"
                >
                  <MenuItem value="Inverter">Inverter</MenuItem>
                  <MenuItem value="Energy Meter">Energy Meter</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                {errors.billedFrom && <span style={{ color: 'red', fontSize: '0.75rem' }}>{errors.billedFrom}</span>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <TextField
                  fullWidth
                  label="Energy Value (kWh)"
                  type="number"
                  value={formData.actualGeneration}
                  onChange={(e) => handleChange('actualGeneration', e.target.value)}
                  error={!!errors.actualGeneration}
                  helperText={errors.actualGeneration || actualGenHelper}
                  placeholder={formData.actualGeneration === '' ? '0' : ''}
                  disabled={financialFieldsLocked}
                />
                {originalValues.actualGeneration !== null &&
                  formData.actualGeneration !== originalValues.actualGeneration && (
                    <Tooltip title={`dgr value: ${originalValues.actualGeneration}`}>
                      <IconButton size="small" sx={{ mt: 1 }}>
                        <InfoIcon fontSize="small" color="info" />
                      </IconButton>
                    </Tooltip>
                  )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Deemed Generation (kWh)"
                type="number"
                value={formData.deemedGeneration}
                onChange={(e) => handleChange('deemedGeneration', e.target.value)}
                disabled={financialFieldsLocked}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tariff"
                type="number"
                value={formData.tariff}
                onChange={(e) => handleChange('tariff', e.target.value)}
                disabled={financialFieldsLocked}
                error={!!errors.tariff}
                helperText={errors.tariff}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Invoice Value (Amount)"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    disabled={financialFieldsLocked}
                    error={!!errors.amount}
                    helperText={errors.amount || "Amount = (Actual Generation + Deemed Generation) * Tariff"}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.currency}
                      onChange={(e) => handleChange('currency', e.target.value)}
                      label="Currency"
                    >
                      <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                      <MenuItem value="AED">AED - United Arab Emirates Dirham</MenuItem>
                      <MenuItem value="BWP">BWP - Botswana Pula</MenuItem>
                      <MenuItem value="TZS">TZS - Tanzanian Shilling</MenuItem>
                      <MenuItem value="XOF">XOF - West African CFA Franc</MenuItem>
                      <MenuItem value="KES">KES - Kenyan Shilling</MenuItem>
                      <MenuItem value="USD">USD - United States Dollar</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="Regular">Regular</MenuItem>
                  <MenuItem value="Additional">Additional</MenuItem>
                  <MenuItem value="Deemed">Deemed</MenuItem>
                  <MenuItem value="EMI">EMI</MenuItem>
                  <MenuItem value="PPA">PPA</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={mode === 'edit' && financialFieldsLocked}>
                <InputLabel>Invoice Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Payment Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                  disabled={mode === 'edit' && financialFieldsLocked}
                >
                  {/* ✅ CHANGED: Only show these two options for new records */}
                  <MenuItem value="To Be Verified">To Be Verified</MenuItem>
                  <MenuItem value="To Be Raised">To Be Raised</MenuItem>
                  {/* Show all options in edit mode for existing records */}
                  {/* {mode === 'edit' && (
                    <>
                      <MenuItem value="Paid">Paid</MenuItem>
                      <MenuItem value="Unpaid">Unpaid</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                    </>
                  )} */}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={3}
                value={formData.remark}
                onChange={(e) => handleChange('remark', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (mode === 'add' ? 'Create' : 'Update')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Confirmation Dialog for Status Change to "To Be Raised" */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelStatusChange}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Are you sure you want to verify this Billing Record? Billing details cannot be changed again and this will allow Finance Team to generate the invoice.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStatusChange} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmStatusChange} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BillingForm
